import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Button,
  Badge,
  Modal,
} from "react-bootstrap";
import moment from "moment";
import { useSelector } from "react-redux";
import branchApi from "../../services/branchapi";
import reportApi from "../../services/reportapi";
import securityApi from "../../services/securityapi";
import { selectCurrentUser, selectAccessData } from "../../features/auth/authSlice";

function Dashboard() {
  const user = useSelector(selectCurrentUser);
  const accessDataStr = useSelector(selectAccessData);

  const accessData = useMemo(() => {
    try {
      return accessDataStr ? JSON.parse(accessDataStr) : {};
    } catch (e) {
      return {};
    }
  }, [accessDataStr]);

  const roleId = Number(accessData?.roleId || 0);
  const dashboardCardsEnabled = Number(accessData?.dashboardCardsEnabled || 0);

  // ✅ login branch from JWT-decoded user (redux state)
  const loginBranchId = Number(user?.branchId || 0) || null;

  // ✅ same roles like OrderTable: can view ALL branches
  const ALL_BRANCH_ROLES = useMemo(() => new Set([1, 2, 8, 10, 12]), []);
  const canViewAllBranches = ALL_BRANCH_ROLES.has(Number(roleId));

  const isSuperAdmin = Number(roleId) === 1;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [allBranches, setAllBranches] = useState([]);
  const [branchId, setBranchId] = useState(""); // used when canViewAllBranches
  const [summary, setSummary] = useState(null);

  const [fromDate, setFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));

  // UI state
  const [showCards, setShowCards] = useState(false);
  const [cardsUnlocked, setCardsUnlocked] = useState(false);

  // password modal
  const [pwdModal, setPwdModal] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwdErr, setPwdErr] = useState(null);
  const [pwdLoading, setPwdLoading] = useState(false);

  // ✅ only these users can call summary api
  const canFetchSummary =
    isSuperAdmin || (dashboardCardsEnabled === 1 && cardsUnlocked);

  // ✅ lock default branch selection for users who cannot view all
  useEffect(() => {
    // if user cannot view all, lock to login branch
    if (!canViewAllBranches && loginBranchId) {
      setBranchId(String(loginBranchId));
    }

    // default cards behavior
    if (isSuperAdmin) {
      setCardsUnlocked(true);
      setShowCards(true);
    } else {
      setCardsUnlocked(false);
      setShowCards(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAllBranches, isSuperAdmin, loginBranchId]);

  const titleRange = useMemo(() => {
    return `${moment(fromDate).format("DD MMM YYYY")} - ${moment(toDate).format(
      "DD MMM YYYY"
    )}`;
  }, [fromDate, toDate]);

  const fmtMoney = (n) => {
    const num = Number(n || 0);
    return `₹ ${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const normalizeBranchList = (resp) => {
    const r = resp?.data;
    const candidate =
      r?.data?.data ??
      r?.data?.result ??
      r?.data ??
      r?.result ??
      r ??
      [];
    if (Array.isArray(candidate)) return candidate;
    if (Array.isArray(candidate?.rows)) return candidate.rows;
    if (Array.isArray(candidate?.list)) return candidate.list;
    return [];
  };

  const fetchBranches = async () => {
    try {
      const resp = await branchApi.getAllBranches({ status: 1 });
      const list = normalizeBranchList(resp);
      setAllBranches(list);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Effective branchId rules:
  // roleId in [1,2,8,10,12] => can send selected branchId, or 0/null for All
  // others => always loginBranchId
  const getEffectiveBranchId = () => {
    if (canViewAllBranches) {
      return branchId ? Number(branchId) : 0; // ✅ 0 == all branches (same like OrderTable)
    }
    return loginBranchId ? Number(loginBranchId) : 0;
  };

  const fetchSummary = async (force = false) => {
    // ✅ hard block (prevents leak)
    // force=true only used immediately after successful password verify
    if (!force && !canFetchSummary) {
      setSummary(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const effectiveBranchId = getEffectiveBranchId();

      const resp = await reportApi.getDashboardSummary({
        branchId: effectiveBranchId,
        fromDate,
        toDate,
      });

      const payload = resp?.data?.data ?? resp?.data ?? null;
      setSummary(payload);
    } catch (e) {
      setError(e?.message || "Failed to load dashboard summary");
    } finally {
      setLoading(false);
    }
  };


  const styles = {
    card: (bg1, bg2) => ({
      border: "0",
      borderRadius: 14,
      color: "#fff",
      background: `linear-gradient(135deg, ${bg1}, ${bg2})`,
      boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
      overflow: "hidden",
      position: "relative",
      minHeight: 110,
    }),
    glow: {
      position: "absolute",
      right: -40,
      top: -40,
      width: 120,
      height: 120,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.18)",
      filter: "blur(2px)",
    },
    label: { fontSize: 13, opacity: 0.9 },
    value: { fontSize: 26, fontWeight: 800, lineHeight: 1.1, marginTop: 6 },
    sub: { fontSize: 12, opacity: 0.9, marginTop: 6 },
  };

  const razorGross = summary?.razorpayGross ?? summary?.totalRazorpay ?? 0;
  const razorRefund = summary?.razorpayRefunded ?? summary?.totalRefunded ?? 0;
  const razorNet =
    summary?.razorpayNet ??
    Number(razorGross || 0) - Number(razorRefund || 0);

  const cards = [
    {
      title: "Total Orders",
      value: summary?.totalOrders ?? 0,
      bg: ["#3b82f6", "#06b6d4"],
      icon: "📦",
    },
    {
      title: "Total Sales",
      value: fmtMoney(summary?.totalSales),
      bg: ["#22c55e", "#16a34a"],
      icon: "💰",
    },
    {
      title: "COD Amount",
      value: fmtMoney(summary?.totalCOD),
      bg: ["#f97316", "#f59e0b"],
      icon: "💵",
    },
    {
      title: "Razorpay Net",
      value: fmtMoney(razorNet),
      bg: ["#8b5cf6", "#ec4899"],
      icon: "⚡",
      sub: `Gross: ${fmtMoney(razorGross)} | Refunded: ${fmtMoney(
        razorRefund
      )}`,
    },
    {
      title: "Refunded Amount",
      value: fmtMoney(summary?.totalRefunded),
      bg: ["#ef4444", "#f43f5e"],
      icon: "↩️",
    },
  ];

  const selectedBranchLabel = useMemo(() => {
    if (!canViewAllBranches) {
      const b = allBranches?.find(
        (x) => String(x.branchId || x.id) === String(loginBranchId)
      );
      return b ? b.branchName || b.name : `Branch ${loginBranchId || ""}`;
    }

    if (!branchId || Number(branchId) === 0) return "All Branches";
    const b = allBranches?.find(
      (x) => String(x.branchId || x.id) === String(branchId)
    );
    return b ? b.branchName || b.name : `Branch ${branchId}`;
  }, [canViewAllBranches, allBranches, branchId, loginBranchId]);

  const handleToggleCards = (next) => {
    if (!next) {
      setShowCards(false);
      return;
    }

    if (isSuperAdmin) {
      setShowCards(true);
      return;
    }

    if (dashboardCardsEnabled !== 1) return;

    if (cardsUnlocked) {
      setShowCards(true);
      return;
    }

    setPwd("");
    setPwdErr(null);
    setPwdModal(true);
  };

  const handleApply = () => {
    if (!canFetchSummary) return;
    fetchSummary(false);
  };

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Dashboard</h4>
          <small className="text-muted">{titleRange}</small>
        </div>
      </div>

      {/* Filters */}
      <Row className="mt-3 g-2 align-items-end">
        <Col xs={12} md={3}>
          <Form.Label className="mb-1">From</Form.Label>
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            max={toDate}
          />
        </Col>

        <Col xs={12} md={3}>
          <Form.Label className="mb-1">To</Form.Label>
          <Form.Control
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            min={fromDate}
          />
        </Col>

        <Col xs={12} md={3}>
          <Form.Label className="mb-1">Branch</Form.Label>
          <Form.Select
            value={branchId}
            disabled={!canViewAllBranches}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {/* ✅ Only show All option to roles who canViewAllBranches */}
            {canViewAllBranches && <option value={0}>All Branches</option>}
            {!canViewAllBranches && (
              <option value={loginBranchId || ""}>
                {selectedBranchLabel}
              </option>
            )}
            {allBranches?.map((b) => (
              <option key={b.branchId || b.id} value={b.branchId || b.id}>
                {b.branchName || b.name}
              </option>
            ))}
          </Form.Select>

          {!canViewAllBranches && (
            <div className="text-muted" style={{ fontSize: 12 }}>
              Branch locked by your role
            </div>
          )}
        </Col>

        <Col xs={12} md={3}>
          <Button
            className="w-100"
            onClick={handleApply}
            disabled={loading || !canFetchSummary}
            title={!canFetchSummary ? "Unlock revenue cards to load summary" : ""}
          >
            {loading ? "Loading..." : "Apply"}
          </Button>

          {!canFetchSummary && !isSuperAdmin && (
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              Unlock revenue cards to load revenue summary.
            </div>
          )}
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {/* Cards toggle */}
      <div className="mt-3">
        {isSuperAdmin || dashboardCardsEnabled === 1 ? (
          <Form.Check
            type="switch"
            id="toggleRevenueCards"
            label="Show Revenue Cards"
            checked={showCards}
            onChange={(e) => handleToggleCards(e.target.checked)}
          />
        ) : (
          !isSuperAdmin && (
            <Alert variant="warning" className="mt-2">
              Revenue cards access is not enabled for your login. Please contact
              Super Admin.
            </Alert>
          )
        )}
      </div>

      {/* Cards */}
      {showCards ? (
        canFetchSummary ? (
          <Row className="mt-3 g-3">
            {cards.map((c) => (
              <Col key={c.title} xs={12} sm={6} lg={4} xl={3}>
                <Card style={styles.card(c.bg[0], c.bg[1])}>
                  <div style={styles.glow} />
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div style={styles.label}>{c.title}</div>
                        <div style={styles.value}>
                          {loading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            c.value
                          )}
                        </div>
                        {c.sub && <div style={styles.sub}>{c.sub}</div>}
                      </div>
                      <div style={{ fontSize: 26, opacity: 0.95 }}>
                        {c.icon}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Alert variant="secondary" className="mt-3">
            Revenue cards are locked. Please unlock to load revenue data.
          </Alert>
        )
      ) : (
        <Alert variant="secondary" className="mt-3">
          Revenue cards are hidden.
        </Alert>
      )}

      {/* Footer info */}
      {summary?.from && summary?.to && (
        <div className="mt-3 text-muted" style={{ fontSize: 12 }}>
          Range: {summary.from} to {summary.to}{" "}
          <Badge bg="secondary" className="ms-1">
            {selectedBranchLabel}
          </Badge>
        </div>
      )}

      {/* Password Modal */}
      <Modal
        show={pwdModal}
        onHide={() => !pwdLoading && setPwdModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Enter Password</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Dashboard Cards Password</Form.Label>
            <Form.Control
              type="password"
              name="dashboard_cards_pwd"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Enter password"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !pwdLoading && pwd) {
                  e.preventDefault();
                  document.getElementById("unlockBtn")?.click();
                }
              }}
            />
          </Form.Group>
          {pwdErr && <div className="text-danger mt-2">{pwdErr}</div>}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={pwdLoading}
            onClick={() => setPwdModal(false)}
          >
            Cancel
          </Button>

          <Button
            id="unlockBtn"
            variant="primary"
            disabled={pwdLoading || !pwd}
            onClick={async () => {
              setPwdLoading(true);
              setPwdErr(null);

              try {
                const resp = await securityApi.verifyDashboardCardsPassword(pwd);

                const status = resp?.status;
                const verified = resp?.data?.verified === true;

                if (status !== "SUCCESS" || !verified) {
                  setPwdErr(resp?.message || "Invalid password");
                  return;
                }

                // ✅ unlock + show
                setCardsUnlocked(true);
                setShowCards(true);
                setPwdModal(false);
                setPwd("");
                setPwdErr(null);

                // ✅ NOW call summary api immediately (force bypasses old canFetchSummary closure)
                fetchSummary(true);
              } catch (e) {
                setPwdErr(
                  e?.response?.data?.message || e?.message || "Verification failed"
                );
              } finally {
                setPwdLoading(false);
              }
            }}
          >
            {pwdLoading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Verifying...
              </>
            ) : (
              "Unlock"
            )}
          </Button>

        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Dashboard;
